import { Host, h } from "@stencil/core";
import { generateRandomId } from "../../utilities/stringUtilities";
/** A custom input component that wraps the html input element is a mobile friendly component that supports a label, some help text and other features.
 * @slot prefix - Can be used to inject content before the input field.
 * @slot suffix - Can be used to inject content after the input field.
 */
export class DnnInput {
    constructor() {
        /** The input type, supports most of html standard input type, see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#input_types. */
        this.type = "text";
        /** The value of the input. */
        this.value = "";
        /** Defines the type of auto-completion to use for this field, see https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete. */
        this.autocomplete = "off";
        this.focused = false;
        this.valid = true;
    }
    /** Reports the input validity details. See https://developer.mozilla.org/en-US/docs/Web/API/ValidityState */
    async checkValidity() {
        var validity = this.inputField.checkValidity();
        if (!validity) {
            this.fieldset.setValidity(false, this.inputField.validationMessage);
        }
        return this.inputField.validity;
    }
    /** Can be used to set a custom validity message. */
    async setCustomValidity(message) {
        if (message == undefined || message == "") {
            this.inputField.setCustomValidity("");
            this.valid = true;
            this.fieldset.setValidity(true);
            return;
        }
        this.inputField.setCustomValidity(message);
        this.valid = false;
        this.fieldset.setValidity(false, message);
    }
    componentWillLoad() {
        this.labelId = generateRandomId();
    }
    componentDidLoad() {
        requestAnimationFrame(() => {
            var validity = this.inputField.validity;
            var validityMessage = validity.valid ? "" : this.inputField.validationMessage;
            this.internals.setValidity(this.inputField.validity, validityMessage);
        });
    }
    formResetCallback() {
        this.inputField.setCustomValidity("");
        this.value = "";
        this.internals.setValidity({});
        this.internals.setFormValue("");
    }
    handleInput(e) {
        if (this.type === "number" && e.data === "-") {
            // Ignore the minus sign if the input type is number
            return;
        }
        var value = e.target.value;
        this.value = value;
        var valid = this.inputField.checkValidity();
        this.valid = valid;
        this.valueInput.emit(this.value);
    }
    handleInvalid() {
        this.valid = false;
        this.fieldset.setValidity(false, this.inputField.validationMessage);
        this.internals.setValidity(this.inputField.validity, this.inputField.validationMessage);
    }
    handleChange() {
        this.valueChange.emit(this.value);
        if (this.name != undefined) {
            var data = new FormData();
            data.append(this.name, this.value.toString());
            this.internals.setFormValue(data);
        }
    }
    switchPasswordVisibility() {
        if (this.type === "password") {
            this.type = "text";
            return;
        }
        if (this.type === "text") {
            this.type = "password";
            return;
        }
    }
    shouldLabelFloat() {
        if (this.type === "number" && isNaN(this.value)) {
            return true;
        }
        if (this.focused) {
            return false;
        }
        if (this.value != undefined && this.value != "") {
            return false;
        }
        if (this.type == "date" || this.type == "datetime-local" || this.type == "time") {
            return false;
        }
        if ((typeof this.value === "string" && this.value === "0") || (typeof this.value === "number" && this.value === 0)) {
            return false;
        }
        return true;
    }
    getInputMode() {
        if (this.inputmode != undefined) {
            return this.inputmode;
        }
        if (this.type === "number" && this.min != undefined) {
            var min = parseFloat(this.min.toString());
            if ((this.step === 1 || this.step == undefined) && min >= 0) {
                return "numeric";
            }
            return "decimal";
        }
        if (this.type === "tel") {
            return "tel";
        }
        if (this.type === "url") {
            return "url";
        }
        if (this.type === "email") {
            return "email";
        }
        if (this.type === "search") {
            return "search";
        }
        return "text";
    }
    handleBlur() {
        this.focused = false;
        var validity = this.inputField.checkValidity();
        this.valid = validity;
        this.fieldset.setValidity(validity, this.inputField.validationMessage);
        this.internals.setValidity(this.inputField.validity, this.inputField.validationMessage);
    }
    render() {
        var _a;
        return (h(Host, { key: 'b0b63e3c3f47f710dee872696c1e83afd4878838', tabIndex: this.focused ? -1 : 0, onFocus: () => this.inputField.focus(), onBlur: () => this.inputField.blur() }, h("dnn-fieldset", { key: '15f583513802a107942f7c7020226ea6284b8482', ref: el => this.fieldset = el, invalid: !this.valid, focused: this.focused, label: `${(_a = this.label) !== null && _a !== void 0 ? _a : ""}${this.required ? " *" : ""}`, helpText: this.helpText, id: this.labelId, disabled: this.disabled, floatLabel: this.shouldLabelFloat(), onClick: () => !this.focused && this.inputField.focus(), onFocus: () => this.focused = true, onBlur: () => this.focused = false }, h("div", { key: 'eec28f3823d8a45f3f9fad0a151115a445f6da75', class: "inner-container" }, !this.shouldLabelFloat() &&
            h("slot", { key: '01ca8208c9c23da0c37da7b16904283d2c16de42', name: "prefix" }), h("input", { key: 'd331914c33c710d34f67863ba05571e9ceb62765', ref: el => this.inputField = el, name: this.name, type: this.type, inputMode: this.getInputMode(), disabled: this.disabled, required: this.required, autoComplete: this.autocomplete, min: this.min, max: this.max, minlength: this.minlength, maxlength: this.maxlength, multiple: this.multiple, pattern: this.pattern, readonly: this.readonly, step: this.step, value: this.value, onBlur: () => this.handleBlur(), onFocus: () => this.focused = true, onInput: e => this.handleInput(e), onInvalid: () => this.handleInvalid(), onChange: () => this.handleChange(), "aria-labelledby": this.labelId }), !this.shouldLabelFloat() &&
            h("slot", { key: 'fc128e67816832dc982b5f20d864eebf649564e2', name: "suffix" }), !this.valid &&
            h("svg", { key: 'd3e897352d6f14fb3b363e5b116c8b88cf0994b0', xmlns: "http://www.w3.org/2000/svg", viewBox: "0 -960 960 960", class: "error" }, h("path", { key: 'fddd3cbb5729e82880ad9cf16ebbbe5fddd6966e', d: "M479.982-280q14.018 0 23.518-9.482 9.5-9.483 9.5-23.5 0-14.018-9.482-23.518-9.483-9.5-23.5-9.5-14.018 0-23.518 9.482-9.5 9.483-9.5 23.5 0 14.018 9.482 23.518 9.483 9.5 23.5 9.5ZM453-433h60v-253h-60v253Zm27.266 353q-82.734 0-155.5-31.5t-127.266-86q-54.5-54.5-86-127.341Q80-397.681 80-480.5q0-82.819 31.5-155.659Q143-709 197.5-763t127.341-85.5Q397.681-880 480.5-880q82.819 0 155.659 31.5Q709-817 763-763t85.5 127Q880-563 880-480.266q0 82.734-31.5 155.5T763-197.684q-54 54.316-127 86Q563-80 480.266-80Zm.234-60Q622-140 721-239.5t99-241Q820-622 721.188-721 622.375-820 480-820q-141 0-240.5 98.812Q140-622.375 140-480q0 141 99.5 240.5t241 99.5Zm-.5-340Z" })), this.allowShowPassword &&
            h("button", { key: 'e8b335697f680543536ae6004c5872de7dbb6aa8', class: "show-password", onClick: () => this.switchPasswordVisibility() }, this.type === "text" &&
                h("svg", { key: '9a689308b001438e5ba21dcb1ccfa74df38a799e', xmlns: "http://www.w3.org/2000/svg", viewBox: "0 -960 960 960" }, h("path", { key: '4fb17fb4580aeb05b4daa882e358b6c38fb89440', d: "M480.118-330Q551-330 600.5-379.618q49.5-49.617 49.5-120.5Q650-571 600.382-620.5q-49.617-49.5-120.5-49.5Q409-670 359.5-620.382q-49.5 49.617-49.5 120.5Q310-429 359.618-379.5q49.617 49.5 120.5 49.5Zm-.353-58Q433-388 400.5-420.735q-32.5-32.736-32.5-79.5Q368-547 400.735-579.5q32.736-32.5 79.5-32.5Q527-612 559.5-579.265q32.5 32.736 32.5 79.5Q592-453 559.265-420.5q-32.736 32.5-79.5 32.5ZM480-200q-146 0-264-83T40-500q58-134 176-217t264-83q146 0 264 83t176 217q-58 134-176 217t-264 83Zm0-300Zm-.169 240Q601-260 702.5-325.5 804-391 857-500q-53-109-154.331-174.5-101.332-65.5-222.5-65.5Q359-740 257.5-674.5 156-609 102-500q54 109 155.331 174.5 101.332 65.5 222.5 65.5Z" })), this.type == "password" &&
                h("svg", { key: 'cb8c6be712364ac3f9aeab43f77ef21455dd0c3c', xmlns: "http://www.w3.org/2000/svg", viewBox: "0 -960 960 960" }, h("path", { key: '84ac1064433d86cde3cf439ef8a5c43f8750c766', d: "m629-419-44-44q26-71-27-118t-115-24l-44-44q17-11 38-16t43-5q71 0 120.5 49.5T650-500q0 22-5.5 43.5T629-419Zm129 129-40-40q49-36 85.5-80.5T857-500q-50-111-150-175.5T490-740q-42 0-86 8t-69 19l-46-47q35-16 89.5-28T485-800q143 0 261.5 81.5T920-500q-26 64-67 117t-95 93Zm58 226L648-229q-35 14-79 21.5t-89 7.5q-146 0-265-81.5T40-500q20-52 55.5-101.5T182-696L56-822l42-43 757 757-39 44ZM223-654q-37 27-71.5 71T102-500q51 111 153.5 175.5T488-260q33 0 65-4t48-12l-64-64q-11 5-27 7.5t-30 2.5q-70 0-120-49t-50-121q0-15 2.5-30t7.5-27l-97-97Zm305 142Zm-116 58Z" })))))));
    }
    static get is() { return "dnn-input"; }
    static get encapsulation() { return "shadow"; }
    static get formAssociated() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["dnn-input.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["dnn-input.css"]
        };
    }
    static get properties() {
        return {
            "type": {
                "type": "string",
                "attribute": "type",
                "mutable": true,
                "complexType": {
                    "original": "\"date\" | \"datetime-local\" | \"email\" | \"number\" | \"password\" | \"tel\" | \"text\" | \"time\" | \"url\" | \"search\"",
                    "resolved": "\"date\" | \"datetime-local\" | \"email\" | \"number\" | \"password\" | \"search\" | \"tel\" | \"text\" | \"time\" | \"url\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The input type, supports most of html standard input type, see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#input_types."
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "\"text\""
            },
            "label": {
                "type": "string",
                "attribute": "label",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The label for this input."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "name": {
                "type": "string",
                "attribute": "name",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The name for this input when used in forms."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "value": {
                "type": "any",
                "attribute": "value",
                "mutable": true,
                "complexType": {
                    "original": "number | string | string[]",
                    "resolved": "number | string | string[]",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The value of the input."
                },
                "getter": false,
                "setter": false,
                "reflect": true,
                "defaultValue": "\"\""
            },
            "helpText": {
                "type": "string",
                "attribute": "help-text",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Defines the help label displayed under the field."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "required": {
                "type": "boolean",
                "attribute": "required",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Defines whether the field requires having a value."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "disabled": {
                "type": "boolean",
                "attribute": "disabled",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Defines whether the field is disabled."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "autocomplete": {
                "type": "string",
                "attribute": "autocomplete",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Defines the type of auto-completion to use for this field, see https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete."
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "\"off\""
            },
            "min": {
                "type": "any",
                "attribute": "min",
                "mutable": false,
                "complexType": {
                    "original": "number | string",
                    "resolved": "number | string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Defines the minimum allowed value."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "max": {
                "type": "any",
                "attribute": "max",
                "mutable": false,
                "complexType": {
                    "original": "number | string",
                    "resolved": "number | string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Defines the maximum allowed value."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "minlength": {
                "type": "number",
                "attribute": "minlength",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Defines the minimum amount of charaters."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "maxlength": {
                "type": "number",
                "attribute": "maxlength",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Defines the maximum amount of charaters."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "multiple": {
                "type": "boolean",
                "attribute": "multiple",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "If true, allows multiple emails to be entered separated by commas."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "pattern": {
                "type": "string",
                "attribute": "pattern",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Valid for text, search, url, tel, email, and password, the pattern attribute defines a regular expression that the input's value must match in order for the value to pass constraint validation."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "readonly": {
                "type": "boolean",
                "attribute": "readonly",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Defines wheter the defined value is readonly."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "step": {
                "type": "any",
                "attribute": "step",
                "mutable": false,
                "complexType": {
                    "original": "string | number",
                    "resolved": "number | string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Defines the possible steps for numbers and dates/times. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/date#step"
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "disableValidityReporting": {
                "type": "boolean",
                "attribute": "disable-validity-reporting",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "deprecated",
                            "text": "This control has it's own validation reporting, will be removed in v0.25.0"
                        }],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "allowShowPassword": {
                "type": "boolean",
                "attribute": "allow-show-password",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "If true, enables users to switch between a password and a text field (to view their password)."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "inputmode": {
                "type": "string",
                "attribute": "inputmode",
                "mutable": false,
                "complexType": {
                    "original": "\"none\" | \"text\" | \"tel\" | \"url\" | \"email\" | \"numeric\" | \"decimal\" | \"search\"",
                    "resolved": "\"decimal\" | \"email\" | \"none\" | \"numeric\" | \"search\" | \"tel\" | \"text\" | \"url\" | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Hints at the type of data that might be entered by the user while editing the element or its contents.\nThis allows a browser to display an appropriate virtual keyboard."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "focused": {},
            "valid": {}
        };
    }
    static get events() {
        return [{
                "method": "valueChange",
                "name": "valueChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fires when the value has changed and the user exits the input."
                },
                "complexType": {
                    "original": "number | string | string[]",
                    "resolved": "number | string | string[]",
                    "references": {}
                }
            }, {
                "method": "valueInput",
                "name": "valueInput",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fires when the using is inputing data (on keystrokes)."
                },
                "complexType": {
                    "original": "number | string | string[]",
                    "resolved": "number | string | string[]",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "checkValidity": {
                "complexType": {
                    "signature": "() => Promise<ValidityState>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "ValidityState": {
                            "location": "global",
                            "id": "global::ValidityState"
                        }
                    },
                    "return": "Promise<ValidityState>"
                },
                "docs": {
                    "text": "Reports the input validity details. See https://developer.mozilla.org/en-US/docs/Web/API/ValidityState",
                    "tags": []
                }
            },
            "setCustomValidity": {
                "complexType": {
                    "signature": "(message: string) => Promise<void>",
                    "parameters": [{
                            "name": "message",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Can be used to set a custom validity message.",
                    "tags": []
                }
            }
        };
    }
    static get attachInternalsMemberName() { return "internals"; }
}
//# sourceMappingURL=dnn-input.js.map
